/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Anaplan", description = "Learn how to sync data from your warehouse to Anaplan using Hightouch.", lastUpdatedDate = new Date(1781049600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Anaplan",
        description: "Learn how to sync data from your warehouse to Anaplan using Hightouch.",
        lastUpdatedDate: "Wed Jun 10 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "anaplan",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Anaplan",
          "depth": 0,
          "id": "connect-to-anaplan"
        }, {
          "value": "OAuth",
          "depth": 1,
          "id": "oauth"
        }, {
          "value": "Basic auth",
          "depth": 1,
          "id": "basic-auth"
        }, {
          "value": "Prerequisites",
          "depth": 0,
          "id": "prerequisites"
        }, {
          "value": "Create an import data source",
          "depth": 1,
          "id": "create-an-import-data-source"
        }, {
          "value": "Create an import (optional)",
          "depth": 1,
          "id": "create-an-import-optional"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Records",
          "depth": 1,
          "id": "records"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      h3: "h3",
      h4: "h4",
      code: "code",
      img: "img",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {PremiumDestination, RowAndColumnOrderingInfo, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!RowAndColumnOrderingInfo) _missingMdxReference("RowAndColumnOrderingInfo", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Anaplan"
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "With the Anaplan destination, you can automate file uploads and trigger import actions in Anaplan. You can sync a variety of data from your warehouse, such as purchase orders, customer data, and more."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#records",
                children: _jsx(_components.strong, {
                  children: "Records"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Anaplan records"
            }), _jsx(_components.td, {
              children: "Insert, All"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://help.anaplan.com/5ef6eaab-72d9-43de-91b3-3f4dcc4711e2-Perform-an-import-action",
                children: "Perform an import action ↗"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(RowAndColumnOrderingInfo, {
        variant: "generic"
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-anaplan",
        children: "Connect to Anaplan"
      }), "\n", _jsx(_components.p, {
        children: "Select an authentication method: OAuth or basic auth (username and password)."
      }), "\n", _jsx(_components.h3, {
        id: "oauth",
        children: "OAuth"
      }), "\n", _jsx(_components.h4, {
        id: "setup-anaplan-oauth-client",
        children: "Setup Anaplan OAuth client"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports Anaplan OAuth clients that have the type ", _jsx(_components.code, {
          children: "Authorization code grant"
        }), " with both non-rotatable and rotatable refresh tokens."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-oauth-client.png",
          alt: "OAuth client in the Anaplan UI"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Non-rotatable: The refresh token used for getting access tokens do not expire for as long as you allow the connection."
        }), "\n", _jsx(_components.li, {
          children: "Rotatable: The refresh token used for getting access tokens changes every time a new access token is retrieved."
        }), "\n"]
      }), "\n", _jsxs(Warning, {
        children: [_jsxs(_components.p, {
          children: ["Rotatable tokens require setting a ", _jsx(_components.code, {
            children: "Refresh token lifespan"
          }), " in Anaplan. This\nsetting is the total time in seconds before the connection will need to be\nre-authenticated in Hightouch."]
        }), _jsx("br", {}), _jsx("br", {}), _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Ex:"
          }), " Setting ", _jsx(_components.code, {
            children: "Refresh token lifespan"
          }), " token to 604800 (7 days) will rotate the\nrefresh token every time an access token expires but will require you to re-authenticate\nyour account at the end of that time. Until you re-authenticate all syncs will\ncontinue to fail. ", _jsx(_components.a, {
            href: "https://help.anaplan.com/rotate-client-secret-357f0086-5752-435b-a5bd-5eb6797ffc3f",
            children: "More detail on refresh tokens"
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Use Non-rotatable refresh tokens for syncs to minimize chances of sync\nfailures."
        })
      }), "\n", _jsx(_components.h4, {
        id: "connecting-with-hightouch",
        children: "Connecting with Hightouch"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-oauth.png",
          alt: "OAuth in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Select the region you want to connect to and then enter your client ID and client secret.\nYour client ID and secret are provided by Anaplan when you setup your OAuth client."
      }), "\n", _jsx(_components.p, {
        children: "Continue through the auth flow to connect your account."
      }), "\n", _jsx(_components.h3, {
        id: "basic-auth",
        children: "Basic auth"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-basic-auth.png",
          alt: "Basic auth in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Select the region you want to connect to and enter your username and password."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If your Anaplan workspace uses an IP allow list, you must add Hightouch's\nserver IP addresses before connecting. See ", _jsx(_components.a, {
            href: "https://help.anaplan.com/enable-ip-allow-list-9fcc01f4-fcb9-4384-8568-4ebb1bdfe9f9",
            children: "Anaplan's IP allow list\ndocumentation"
          }), "\nand ", _jsx(_components.a, {
            href: "/security/networking#ip-addresses",
            children: "Hightouch's IP addresses"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsx(_components.p, {
        children: "Before creating a sync, set up the following in Anaplan:"
      }), "\n", _jsx(_components.h3, {
        id: "create-an-import-data-source",
        children: "Create an import data source"
      }), "\n", _jsxs(_components.p, {
        children: ["You must create a data source in Anaplan for Hightouch to upload data into. Go to your model's ", _jsx(_components.strong, {
          children: "Model settings → Actions → Import Data Source"
        }), " and create a new import data source by uploading a placeholder CSV."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-import-data-source.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch overwrites this CSV with data from your warehouse based on the mappings in your sync configuration."
      }), "\n", _jsx(_components.h3, {
        id: "create-an-import-optional",
        children: "Create an import (optional)"
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally create imports based on the data source and trigger them after the upload completes. Go to a list and select ", _jsx(_components.strong, {
          children: "Import"
        }), ". After selecting the placeholder data source, you can create an import that can then be selected in Hightouch."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-create-import.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "records",
        children: "Records"
      }), "\n", _jsx(_components.p, {
        children: "Use this sync type to upload data from your warehouse to an Anaplan import data source and optionally trigger imports."
      }), "\n", _jsx(_components.h4, {
        id: "configure-record-matching",
        children: "Configure record matching"
      }), "\n", _jsx(_components.p, {
        children: "Select the model and data source that you created (or an existing one). Hightouch overwrites the import data source on each sync run. You can choose between two modes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": The data source is overwritten with only the rows that have been added since the last run."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All"
          }), ": The data source is overwritten with the full results of the query in this run."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-model.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "configure-triggered-imports",
        children: "Configure triggered imports"
      }), "\n", _jsx(_components.p, {
        children: "You can select one or more imports to trigger after the data source upload completes. By default, Hightouch triggers these imports in parallel and waits for all imports to finish before completing the sync."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-imports.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "map-fields",
        children: "Map fields"
      }), "\n", _jsx(_components.p, {
        children: "Map the columns in your Hightouch model to columns in your Anaplan data source."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "The Anaplan column names you map to in Hightouch must exactly match the column\nnames configured in your Anaplan data source. The columns must also be in the\nsame order as they appear in your data source. Incorrect column names or order\nmismatches cause your sync to error."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-mappings.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Anaplan, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes:\nAnaplan"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes, verify the data in Anaplan:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to your model's ", _jsx(_components.strong, {
            children: "Model settings → Actions → Import Data Source"
          }), " and open the data source you configured. Confirm the file contents match the rows and columns from your Hightouch model."]
        }), "\n", _jsxs(_components.li, {
          children: ["If you configured triggered imports, go to the ", _jsx(_components.a, {
            href: "https://help.anaplan.com/5ef6eaab-72d9-43de-91b3-3f4dcc4711e2-Perform-an-import-action",
            children: "imports page"
          }), " and check that the import status shows success. Failed imports typically indicate a column mismatch between the file and the target list."]
        }), "\n", _jsx(_components.li, {
          children: "Open the target list or module in Anaplan and spot-check that values landed in the correct fields. Column name or ordering mismatches are the most common cause of sync failures — if data appears in the wrong columns, compare your Hightouch field mapping order against the import data source column order."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "connection-failures",
        children: "Connection failures"
      }), "\n", _jsx(_components.p, {
        children: "If your Anaplan connection fails during setup or stops working after initial configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Basic auth:"
          }), " Verify that your username and password are correct and that the account hasn't been locked. If your organization enforces SSO, confirm that the Anaplan user account has SSO disabled for API access — SSO-only accounts can't authenticate via basic auth. If your Anaplan workspace uses an IP allow list, confirm that ", _jsx(_components.a, {
            href: "/security/networking#ip-addresses",
            children: "Hightouch's IP addresses"
          }), " are included."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "OAuth:"
          }), " Confirm that your client ID and secret match the ", _jsx(_components.a, {
            href: "https://help.anaplan.com/rotate-client-secret-357f0086-5752-435b-a5bd-5eb6797ffc3f",
            children: "OAuth client configured in Anaplan"
          }), ". If you're using rotatable refresh tokens, the connection expires after the ", _jsx(_components.code, {
            children: "Refresh token lifespan"
          }), " period you set in Anaplan — re-authenticate the destination in Hightouch when this happens."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Region mismatch:"
          }), " Confirm that the region selected in Hightouch matches your Anaplan workspace region. Using the wrong region causes authentication to fail silently."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "column-name-or-order-mismatch",
        children: "Column name or order mismatch"
      }), "\n", _jsxs(_components.p, {
        children: ["If your sync errors with a message about column mapping, verify that the column names in your Hightouch field mapping ", _jsx(_components.strong, {
          children: "exactly match"
        }), " the column names in your Anaplan import data source, and that the columns are in the ", _jsx(_components.strong, {
          children: "same order"
        }), ". Even small differences in capitalization or spacing cause sync failures."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
